import { useId } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { SystemStyleObject, Theme } from '@mui/system';

import type { GeneratedIntakeHelpPanel } from '../../types';

import { HelpMarkdown } from './HelpPanelDrawer.styles';
import { groupBlurbSx } from './tokens';

interface HelpPanelDrawerProps {
  drawerId: string;
  panel: GeneratedIntakeHelpPanel | null;
  open: boolean;
  onClose: () => void;
  onAction: (action: GeneratedIntakeHelpPanel['support_cta']['action']) => void;
}

function HelpExpando({ title, body, id }: { title: string; body: string; id: string }) {
  return (
    <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary
        id={`${id}-heading`}
        aria-controls={`${id}-content`}
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0 }}
      >
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <HelpMarkdown size="medium" markdown={body} />
      </AccordionDetails>
    </Accordion>
  );
}

export function HelpPanelDrawer({
  drawerId,
  panel,
  open,
  onClose,
  onAction,
}: HelpPanelDrawerProps) {
  const id = useId().replace(/:/g, '');
  const titleId = `intake-help-title-${id}`;
  const definitionId = `intake-help-definition-${id}`;

  if (!panel) return null;

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          id: drawerId,
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': titleId,
          'aria-describedby': definitionId,
          sx: { width: { xs: 'min(100vw, 420px)', sm: 420 }, p: 3 },
        },
      }}
    >
      <Stack sx={{ height: '100%' }} spacing={2}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography id={titleId} component="h2" variant="h6" sx={{ fontSize: '1.5rem' }}>
            {panel.title}
          </Typography>
          <IconButton autoFocus aria-label="Close help" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box id={definitionId}>
          <HelpMarkdown size="medium" markdown={panel.definition} />
        </Box>

        {panel.where_to_find && (
          <HelpExpando
            key={`${panel.key}-where`}
            id={`${id}-where`}
            title="Where do I find it?"
            body={panel.where_to_find}
          />
        )}

        {(panel.what_goes_wrong?.length ?? 0) > 0 && (
          <Box>
            {panel.what_goes_wrong?.map((item, index) => {
              // Existing YAML uses a bold opening question followed by its answer.
              // Leave unstructured legacy copy visible instead of inventing a label.
              const section = item.match(/^\s*\*\*([^\n]+?)\*\*\s*\n+([\s\S]+)$/);
              return section ? (
                <HelpExpando
                  key={`${panel.key}-pitfall-${index}`}
                  id={`${id}-pitfall-${index}`}
                  title={section[1]}
                  body={section[2]}
                />
              ) : (
                <Box key={`${panel.key}-pitfall-${index}`} sx={{ mb: 1 }}>
                  <HelpMarkdown size="medium" markdown={item} />
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onAction(panel.support_cta.action);
          }}
        >
          {panel.support_cta.label}
        </Button>
      </Stack>
    </Drawer>
  );
}

/**
 * The group's blurb: always-visible context under the group header,
 * as distinct from the help panel behind the "?" trigger. Renders nothing when
 * the catalog has no blurb for the group.
 */
export function GroupBlurb({
  blurb,
  component = 'div',
  sx,
}: {
  blurb?: string | null;
  component?: 'div' | 'span';
  sx?: SystemStyleObject<Theme>;
}) {
  if (!blurb) return null;
  return (
    <Box component={component} data-testid="group-blurb" sx={{ ...groupBlurbSx, ...sx }}>
      {blurb}
    </Box>
  );
}

interface GroupHelpTriggerProps {
  groupName: string;
  controlsId?: string;
  expanded: boolean;
  onOpen: (opener: HTMLButtonElement) => void;
}

export function GroupHelpTrigger({
  groupName,
  controlsId,
  expanded,
  onOpen,
}: GroupHelpTriggerProps) {
  return (
    <IconButton
      size="small"
      sx={{ flexShrink: 0, alignSelf: 'center' }}
      aria-label={`Help for ${groupName}`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={expanded ? controlsId : undefined}
      onClick={event => onOpen(event.currentTarget)}
    >
      <HelpOutlineIcon fontSize="small" />
    </IconButton>
  );
}
